import { useCallback, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@shared/api/client'
import { endpoints } from '@shared/api/endpoints'
import { QUERY_CACHE_KEYS } from '@shared/api/queryKeys'
import { buildQueryParams, unwrapList, usePagination, type Paginated } from '@shared/lib'
import {
  PARTICIPANT_TAB_ACTIVE,
  PARTICIPANT_TAB_FINISHED,
  participantListParams,
  type ParticipantOlympiadListParams,
} from '../model/participantListParams'
import type { ParticipantOlympiad } from '../model/types'

/**
 * Whole participant-olympiad set, unpaginated — for consumers that render no
 * pagination UI (dashboard banner, results overview, selects).
 *
 * Deliberately separate from `useParticipantOlympiads`: that hook persists its
 * page in sessionStorage under one shared key, so a screen without pagination
 * controls would silently inherit (and clamp) the olympiad list's current page
 * and request `page=2` for a list it shows in full.
 */
export function useAllParticipantOlympiads(options?: { enabled?: boolean }) {
  return useQuery<ParticipantOlympiad[]>({
    queryKey: QUERY_CACHE_KEYS.olympiads.participantAll,
    queryFn: async () => {
      const r = await apiClient.get<ParticipantOlympiad[] | Paginated<ParticipantOlympiad>>(
        endpoints.olympiads.participantList
      )
      return unwrapList(r.data)
    },
    enabled: options?.enabled ?? true,
  })
}

/**
 * Список одной вкладки без пагинации и без sessionStorage — общая основа
 * фоновых запросов хоста окон и плашки главной. Фоновые: отказ не должен
 * давать глобальный тост «Ошибка» на любой странице кабинета, поэтому
 * `meta.silent`.
 */
function useParticipantTabList(
  tab: Parameters<typeof participantListParams>[0],
  queryKey: readonly unknown[],
  options?: { enabled?: boolean }
) {
  return useQuery<ParticipantOlympiad[]>({
    queryKey,
    queryFn: async () => {
      const r = await apiClient.get<ParticipantOlympiad[] | Paginated<ParticipantOlympiad>>(
        endpoints.olympiads.participantList,
        { params: buildQueryParams(participantListParams(tab)) }
      )
      return unwrapList(r.data)
    },
    enabled: options?.enabled ?? true,
    meta: { silent: true },
  })
}

/**
 * Активные олимпиады участника (`tab=active`: предстоящие и идущие) — для
 * плашки главной и окон при входе. Первая страница ответа: активных у
 * участника единицы, прошедшие сюда не попадают.
 */
export function useActiveParticipantOlympiads(options?: { enabled?: boolean }) {
  return useParticipantTabList(
    PARTICIPANT_TAB_ACTIVE,
    QUERY_CACHE_KEYS.olympiads.participantActive,
    options
  )
}

/**
 * Первая страница завершённых, свежие сверху (`-schedule_start`). Нужна окнам
 * при входе: дисквалифицируют чаще всего уже после тура, когда олимпиада ушла
 * из `tab=active`. Отсечку «недавно» делает потребитель — бэк такого фильтра
 * не даёт.
 */
export function useRecentFinishedParticipantOlympiads(options?: { enabled?: boolean }) {
  return useParticipantTabList(
    PARTICIPANT_TAB_FINISHED,
    QUERY_CACHE_KEYS.olympiads.participantFinishedRecent,
    options
  )
}

export function useParticipantOlympiads(params?: ParticipantOlympiadListParams) {
  const { page, pageSize, setTotalItems, resetPage, ...rest } = usePagination({
    key: QUERY_CACHE_KEYS.olympiads.participantList().join('-'),
  })

  const queryParams = buildQueryParams({ page, page_size: pageSize, ...(params ?? {}) })

  const query = useQuery<Paginated<ParticipantOlympiad>>({
    queryKey: QUERY_CACHE_KEYS.olympiads.participantList(queryParams),
    queryFn: async () => {
      const r = await apiClient.get<ParticipantOlympiad[] | Paginated<ParticipantOlympiad>>(
        endpoints.olympiads.participantList,
        { params: queryParams }
      )

      if (Array.isArray(r.data))
        return { count: r.data.length, results: r.data, next: null, previous: null }

      return r.data
    },
  })

  useEffect(() => {
    if (query.data?.count != null) setTotalItems(query.data.count)
  }, [query.data?.count, setTotalItems])

  return {
    data: query.data?.results ?? [],
    count: query.data?.count ?? 0,
    isLoading: query.isLoading,
    // Момент получения ответа — по нему считается расхождение часов устройства
    // с серверными (`participant_timing.server_now`), чтобы отсчёт на карточках
    // не зависел от сбитых часов участника.
    dataUpdatedAt: query.dataUpdatedAt,
    pagination: { page, pageSize, setTotalItems, resetPage, ...rest },
  }
}

function fetchParticipantOlympiadDetail(id: number): Promise<ParticipantOlympiad> {
  return apiClient
    .get<ParticipantOlympiad>(endpoints.olympiads.participantDetail(id))
    .then((r) => r.data)
}

export function useParticipantOlympiadDetail(id: number | null) {
  return useQuery<ParticipantOlympiad>({
    queryKey: id ? QUERY_CACHE_KEYS.olympiads.participantDetail(id) : [],
    queryFn: () => fetchParticipantOlympiadDetail(id!),
    enabled: id !== null,
    // Права участника меняются на стороне бэка, пока вкладка не в фокусе:
    // открылся вход в прокторинг, наступил старт, изменилась регистрация.
    // Вернувшийся на страницу (в том числе из самого PROCTOR) должен увидеть
    // текущее состояние, а не пятиминутный кеш — глобальный дефолт здесь
    // выключен, поэтому включаем точечно.
    refetchOnWindowFocus: true,
  })
}

/**
 * Свежая деталь олимпиады прямо сейчас, в обход `staleTime`.
 *
 * Нужна ПОСЛЕ отказа 409 `hybrid_route_changed`: узнать, на какой маршрут
 * сервер переразрешил участника, чтобы показать ему новый формат и спросить
 * подтверждение. Пятиминутный кеш для этого не годится — в нём лежит ровно тот
 * маршрут, из-за которого отказ и пришёл.
 *
 * Перед КАЖДОЙ отправкой деталь больше не перезапрашивается: сверку маршрута
 * делает сервер по присланному `participation_format` (проба стенда
 * 2026-08-26 — сверка общая, не только гибридная), и лишний round-trip на
 * счастливом пути был не нужен.
 *
 * Возвращает данные, а не полагается на обновление пропсов: пропсы приедут
 * только со следующим рендером, а решение нужно принять в том же обработчике.
 *
 * Запрос идёт НЕ через `fetchQuery` на ключе страницы, хотя так короче.
 * Ключ там общий с `useParticipantOlympiadDetail`, который держит всю
 * детальную: упавшая проба переводила бы этот кеш в состояние ошибки, и
 * страница, приняв это за битый или чужой `:id`, уводила участника в список
 * прямо посреди заполненной формы (поймано проверкой 2026-08-22). Поэтому
 * ответ кладётся в кеш только при успехе, а отказ остаётся отказом этого
 * промиса.
 */
export function useRefreshParticipantOlympiadDetail(id: number) {
  const qc = useQueryClient()
  return useCallback(
    () =>
      fetchParticipantOlympiadDetail(id).then((fresh) => {
        qc.setQueryData(QUERY_CACHE_KEYS.olympiads.participantDetail(id), fresh)
        return fresh
      }),
    [qc, id]
  )
}
